/**
 * @file npm-guide.ts
 * @description Данные инструкции «Как подключить домен» (phase19.28 этап А;
 * uiux-platform-shops §5; шаблон devops-domains-routing §3.1 — NPM 2.14).
 *
 * Шаблон живёт в одном месте: разметка (`DomainConnectGuide`) только рисует
 * строки. Порядок: DNS у регистратора → хост в NPM (делает владелец
 * платформы, после DNS ✓ — иначе Let's Encrypt откажет, 5 попыток в час) →
 * проверка. IP, базовый домен, цель CNAME и памятка — из edge-config API
 * (ревью В1). У поддомена платформы шаг DNS уже выполнен (wildcard).
 */

import type { PlatformEdgeConfig } from "../api-platform-shops";

/** Строка DNS-записи для копирования. */
export interface DnsRecordLine {
  type: "A" | "CNAME";
  name: string;
  value: string;
}

/** Пункт шага NPM: подпись и значения (значения с копированием). */
export interface NpmGuideLine {
  key: string;
  title: string;
  text: string;
  copy?: string[];
}

export interface DomainGuide {
  /** Главный адрес в NPM — всегда punycode, без `www.`. */
  host: string;
  isSubdomain: boolean;
  dns: DnsRecordLine[];
  /** «или CNAME www → …». */
  cnameHint: string;
  npm: NpmGuideLine[];
  /** Вся памятка одним текстом — кнопка «Скопировать всю памятку». */
  plain: string;
  /** Публичный адрес памятки (если edge-config отдал http-ссылку); путь в репозитории не показываем. */
  docUrl: string | null;
  /** Wildcard платформы: `*.commora.ru`. */
  wildcard: string;
}

/** Адрес без `www.`: главный адрес, `www` — переадресация на него. */
function apexOf(host: string): string {
  return host.startsWith("www.") ? host.slice(4) : host;
}

/** Поля NPM по §3.1: Proxy Host, Custom location /api, SSL, Redirection Host для www. */
function npmLines(apex: string, isSubdomain: boolean): NpmGuideLine[] {
  const lines: NpmGuideLine[] = [
    {
      key: "proxy",
      title: "Proxy Host",
      text: `Domain Names ${apex} · http · chaiberi-web · 3000 · Cache Assets выкл · Block Common Exploits вкл · Websockets вкл`,
      copy: [apex, "chaiberi-web"],
    },
    { key: "location", title: "Custom location", text: "/api → http chaiberi-api : 3001", copy: ["chaiberi-api"] },
    { key: "ssl", title: "SSL", text: "Новый сертификат · Force SSL вкл · HTTP/2 вкл · HSTS ВЫКЛ" },
  ];
  if (!isSubdomain) {
    lines.push({
      key: "redirect",
      title: "Redirection Host",
      text: `www.${apex} → https ${apex} · 301 · Preserve Path вкл · свой сертификат`,
      copy: [`www.${apex}`],
    });
  }
  return lines;
}

export function buildDomainGuide(host: string, kind: "SUBDOMAIN" | "CUSTOM", edge: PlatformEdgeConfig): DomainGuide {
  const apex = apexOf(host);
  const isSubdomain = kind === "SUBDOMAIN";
  // Полное имя, а не «@»: корень зоны по адресу не угадать (spb.ru, com.ru).
  const dns: DnsRecordLine[] = isSubdomain
    ? []
    : [apex, `www.${apex}`].flatMap((name) => edge.publicIps.map((ip): DnsRecordLine => ({ type: "A", name, value: ip })));
  const cnameHint = `или CNAME www → ${edge.cnameTarget ?? apex}`;
  const wildcard = `*.${edge.baseDomain}`;
  const npm = npmLines(apex, isSubdomain);
  const docUrl = /^https?:\/\//.test(edge.npmGuidePath) ? edge.npmGuidePath : null;
  const plain = [
    `Подключение ${apex}`,
    isSubdomain
      ? `1. DNS: готов (wildcard ${wildcard})`
      : `1. DNS у регистратора: ${dns.map((r) => `${r.type} ${r.name} ${r.value}`).join("; ")} (${cnameHint})`,
    "2. NPM (после DNS ✓):",
    ...npm.map((line) => `   ${line.title}: ${line.text}`),
    "3. Проверка в кабинете: «Проверить сейчас» — DNS, прокси, HTTPS",
    ...(docUrl ? [`Подробно: ${docUrl}`] : []),
  ].join("\n");
  return { host: apex, isSubdomain, dns, cnameHint, npm, plain, docUrl, wildcard };
}
